<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import PublicHeader from '@/components/public/PublicHeader.vue'
import PublicFooter from '@/components/public/PublicFooter.vue'

const { t } = useI18n()

const router = useRouter()

const handleHelpClick = () => {
  console.log("Help button clicked")
}

const handleLanguageChange = (language: string) => {
  console.log("Language changed to:", language)
}

const handleFooterLinkClick = (section: string, item: string) => {
  console.log("Footer link clicked:", section, item)
}

const handleRegister = () => {
  console.log("Government registration initiated")
}

const handlePortalAccess = () => {
  console.log("Government portal access requested")
}
</script>

<template>
  <PublicHeader 
    @help-click="handleHelpClick"
    @language-change="handleLanguageChange"
  />

  <main class="min-h-screen bg-gray-50">
    <div class="max-w-4xl mx-auto px-5 py-12">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div class="text-center mb-8">
          <div class="w-16 h-16 bg-yellow-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
            <i class="fas fa-university text-yellow-600 text-2xl"></i>
          </div>
          <h1 class="text-3xl font-bold text-accent mb-2">{{ $t('government.register_page.title') }}</h1>
          <p class="text-gray-600">{{ $t('government.register_page.subtitle') }}</p>
        </div>

        <div class="space-y-6">
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 class="font-semibold text-blue-800 mb-2">{{ $t('government.register_page.dna_requirements.title') }}</h3>
            <ul class="text-blue-700 text-sm space-y-1">
              <li>• {{ $t('government.register_page.dna_requirements.official_designation') }}</li>
              <li>• {{ $t('government.register_page.dna_requirements.unfccc_coordination') }}</li>
              <li>• {{ $t('government.register_page.dna_requirements.legal_authority') }}</li>
              <li>• {{ $t('government.register_page.dna_requirements.article6_mandate') }}</li>
            </ul>
          </div>

          <div class="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 class="font-semibold text-green-800 mb-2">{{ $t('government.register_page.admin_functions.title') }}</h3>
            <ul class="text-green-700 text-sm space-y-1">
              <li>• {{ $t('government.register_page.admin_functions.project_approval') }}</li>
              <li>• {{ $t('government.register_page.admin_functions.registry_oversight') }}</li>
              <li>• {{ $t('government.register_page.admin_functions.transfer_authorization') }}</li>
              <li>• {{ $t('government.register_page.admin_functions.compliance_enforcement') }}</li>
            </ul>
          </div>

          <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 class="font-semibold text-amber-800 mb-2">{{ $t('government.register_page.access_levels.title') }}</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div class="bg-white p-3 rounded border">
                <h4 class="font-medium text-gray-800">{{ $t('government.register_page.access_levels.observer.title') }}</h4>
                <p class="text-sm text-gray-600">{{ $t('government.register_page.access_levels.observer.description') }}</p>
              </div>
              <div class="bg-white p-3 rounded border">
                <h4 class="font-medium text-gray-800">{{ $t('government.register_page.access_levels.administrator.title') }}</h4>
                <p class="text-sm text-gray-600">{{ $t('government.register_page.access_levels.administrator.description') }}</p>
              </div>
            </div>
          </div>

          <div class="text-center pt-6 space-y-4">
            <p class="text-gray-600 mb-6">{{ $t('government.register_page.registration_status') }}</p>
            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                @click="handleRegister"
                class="bg-primary text-white px-8 py-3 rounded-md font-medium hover:bg-primary/90 transition-colors"
              >
                {{ $t('government.register_page.buttons.register_new_dna') }}
              </button>
              <button 
                @click="handlePortalAccess"
                class="bg-yellow-600 text-white px-8 py-3 rounded-md font-medium hover:bg-yellow-700 transition-colors"
              >
                {{ $t('government.register_page.buttons.access_existing_portal') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>

  <PublicFooter 
    @link-click="handleFooterLinkClick"
  />
</template>